import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {SelectField} from '@legit.health/ui';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {Button, Grid, Typography, Container, List, ListItem, Box, styled} from '@mui/material';
import {Alert} from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select';
import {TFunction} from 'i18next';
import useWebcam from '@/shared/hooks/useWebcam';

const StyledVideo = styled('video')({
  width: '100%',
  mb: -2
});

const VideoListItemWebcamMaskFlashing = styled('div')(({theme}) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  height: '100%',
  boxShadow: 'inset 0px 0px 23px 0px black',
  animation: 'flash 4s infinite',
  '@keyframes flash': {
    '0%': {
      backgroundColor: 'transparent'
    },
    '90%': {
      backgroundColor: 'transparent'
    },
    '93%': {
      backgroundColor: theme.palette.common.white
    }
  }
}));

type Props = {
  title: string | ReactNode;
  onStartRecording: (stopRecording: () => void) => void;
  onVideoReady: (video: Blob) => void;
  onProcessingVideo?: () => void;
  onClearVideo?: () => void;
  enableAudioSource: boolean;
  rightColumnInitialComponent?: ReactNode | null;
  mask?: ReactNode;
  t: TFunction;
};

function WebcamRecorder({
  title,
  onStartRecording,
  onVideoReady,
  onProcessingVideo,
  onClearVideo,
  enableAudioSource,
  rightColumnInitialComponent,
  mask,
  t
}: Props) {
  const [isRecording, setIsRecording] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');
  const streamRecorderRef = useRef<null | MediaRecorder>(null);
  const {
    audioSourceOptions,
    videoSourceOptions,
    audioSource,
    setAudioSource,
    videoSource,
    setVideoSource,
    error,
    streamRef,
    videoRef
  } = useWebcam({enableAudioSource});

  const chunks = useRef<any[]>([]);
  const isVideoReady = Boolean(downloadLink);

  function onChangeAudioSource(event: SelectChangeEvent<string | number>) {
    setAudioSource(`${event.target.value}`);
  }

  function onChangeVideoSource(event: SelectChangeEvent<string | number>) {
    setVideoSource(`${event.target.value}`);
  }

  function clearRecordedVideo() {
    URL.revokeObjectURL(downloadLink);
    setDownloadLink('');
    if (onClearVideo) {
      onClearVideo();
    }
  }

  const stopRecording = useCallback(function () {
    if (!streamRecorderRef.current) {
      return;
    }
    streamRecorderRef.current.stop();
    setIsRecording(false);
  }, []);

  function startRecording() {
    if (isRecording) {
      return;
    }
    if (!streamRef.current) {
      return;
    }
    streamRecorderRef.current = new MediaRecorder(streamRef.current);
    streamRecorderRef.current.start();
    streamRecorderRef.current.ondataavailable = function (event: BlobEvent) {
      chunks.current.push(event.data);
    };
    streamRecorderRef.current.onstop = function () {
      if (onProcessingVideo) {
        onProcessingVideo();
      }
      const blob = new Blob(chunks.current, {
        type: streamRecorderRef.current?.mimeType ?? 'video/x-matroska;codecs=avc1,opus'
      });
      setDownloadLink(URL.createObjectURL(blob));
      onVideoReady(blob);
      chunks.current = [];
    };
    setIsRecording(true);
    onStartRecording(stopRecording);
  }

  useEffect(function () {
    return function cleanUp() {
      if (!streamRecorderRef.current) {
        return;
      }
      if (streamRecorderRef.current.state === 'recording') {
        streamRecorderRef.current.stop();
      }
    };
  }, []);

  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          <List disablePadding>
            <ListItem
              divider
              sx={{
                bgcolor: 'secondary.light',
                borderRadius: '16px 16px 0 0'
              }}
            >
              {title}
            </ListItem>
            <ListItem
              disableGutters
              sx={{
                display: isVideoReady ? 'none' : 'block',
                padding: 0
              }}
            >
              <Box
                sx={{
                  position: 'relative'
                }}
              >
                {isRecording && <VideoListItemWebcamMaskFlashing />}
                {mask && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      boxShadow: 'inset 0px 0px 23px 0px black'
                    }}
                  >
                    {mask}
                  </Box>
                )}
                <StyledVideo ref={videoRef} autoPlay muted playsInline />
              </Box>
            </ListItem>
            <ListItem
              disableGutters
              sx={{
                display: isVideoReady ? 'block' : 'none',
                padding: 0
              }}
            >
              <StyledVideo src={downloadLink} controls />
            </ListItem>
            <ListItem
              sx={{
                borderRadius: '0 0 16px 16px',
                bgcolor: 'secondary.light'
              }}
            >
              <Button
                onClick={isVideoReady ? clearRecordedVideo : startRecording}
                startIcon={isVideoReady ? <AutorenewIcon /> : <FiberManualRecordIcon />}
                disabled={isRecording}
                variant="contained"
                disableElevation
                color="secondary"
                fullWidth
                size="large"
              >
                {isVideoReady
                  ? t('shared/common:diagnosticReport.form.video.repeatRecording')
                  : isRecording
                    ? t('shared/common:diagnosticReport.form.video.recording')
                    : t('shared/common:diagnosticReport.form.video.record')}
              </Button>
            </ListItem>
            {downloadLink && (
              <Button href={downloadLink} download="file.mp4" variant="text" fullWidth>
                {t('shared/common:diagnosticReport.form.video.canDownloadVideo')}
              </Button>
            )}
          </List>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}
        >
          {rightColumnInitialComponent}
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: '16px'
            }}
          >
            {t('shared/common:diagnosticReport.form.video.deviceSettings')}
          </Typography>
          {enableAudioSource && audioSourceOptions && (
            <SelectField
              required
              value={audioSource}
              onChange={onChangeAudioSource}
              label={t('shared/common:diagnosticReport.form.video.audioSource')}
              name="audio"
              id="audio"
              options={audioSourceOptions}
            />
          )}
          {videoSourceOptions && (
            <SelectField
              required
              value={videoSource}
              onChange={onChangeVideoSource}
              label={t('shared/common:diagnosticReport.form.video.videoSource')}
              name="video"
              id="video"
              options={videoSourceOptions}
              withEmptyOption={false}
            />
          )}
        </Grid>
      </Grid>
      {error && <Alert severity="error">{error.message}</Alert>}
    </Container>
  );
}

export default WebcamRecorder;
